import { Upgrade } from './Upgrade'
import { usePaymentInfo } from '../payment/usePaymentInformation'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { Redirect } from '@jotta/ui/Redirect'
import { Convert } from './Convert'
import { PrivateLayout } from '../components/Layout/PrivateLayout'
import { useParams, useSearchParams } from 'react-router-dom'
import type { GetPaymentInfoResponse } from '@jotta/grpc-connect-openapi/payment'
import {
  RecurringSubscriptionInterval,
  SubscriptionInterval,
} from '@jotta/grpc-connect-openapi/payment'
import { ConfirmUpgrade } from './ConfirmUpgrade'
import { useGetCustomer } from '@jotta/grpc-connect-client/customer'
import { SubscriptionType } from '@jotta/grpc-connect-openapi/customer'

function resolveInterval(
  queryParam: string | null,
  getPaymentInfoResponse: GetPaymentInfoResponse | undefined,
): RecurringSubscriptionInterval | undefined {
  switch (queryParam) {
    case 'monthly':
      return RecurringSubscriptionInterval.MONTHLY_RECURRING
    case 'yearly':
      return RecurringSubscriptionInterval.YEARLY_RECURRING
  }

  switch (getPaymentInfoResponse?.subscriptionInterval) {
    case undefined:
      return undefined
    case SubscriptionInterval.MONTHLY:
      return RecurringSubscriptionInterval.MONTHLY_RECURRING
    default:
      return RecurringSubscriptionInterval.YEARLY_RECURRING
  }
}

export const UpgradeRoute = ({ convertIntent }: { convertIntent: boolean }) => {
  const { data: getPaymentInfoResponse } = usePaymentInfo()
  const { data: customer } = useGetCustomer()
  const { productCode } = useParams()
  const [search] = useSearchParams()
  const recurringInterval = resolveInterval(
    search.get('interval'),
    getPaymentInfoResponse,
  )
  const changeSubscriptionOption =
    getPaymentInfoResponse?.changeSubscriptionOption
  if (
    !customer ||
    !recurringInterval ||
    !changeSubscriptionOption ||
    !changeSubscriptionOption.case
  ) {
    return <LoadingOverlay open />
  }

  switch (changeSubscriptionOption.case) {
    case 'contactBusinessAdmin':
    case 'manageInAppStore':
    case 'manageInPlayStore':
    case 'contactReseller':
    case 'contactSupport':
    case 'manageSubscriptionExternally':
      return <Redirect to="/web/billing" replace />
    case 'convertFromReseller':
      return <Convert />
    case 'saSubscription':
    case 'normalUpgrade':
    case 'manageInBusinessPages':
    case 'contactFamilyOwner':
      if (convertIntent && customer.subscriptionType != SubscriptionType.FREE) {
        return <Convert />
      }
      return productCode ? (
        <ConfirmUpgrade
          productCode={productCode}
          subscriptionInterval={recurringInterval}
        />
      ) : (
        <PrivateLayout section="upgrade">
          <Upgrade />
        </PrivateLayout>
      )
    default:
      return exhaustiveGuard(changeSubscriptionOption)
  }
}
